<template>
  <div class="check-button">
    <input
      type="checkbox"
      :checked="checked"
      :disabled="disabled"
      :id="id"
      :name="name"
      :value="value"
      @change="onChange"
      />
    <label :for="id" :style="backgroundColor">
      <icon class="icon" :icon="icon" />
      <span><slot /></span>
      <icon class="icon status" size="small" :icon="statusIcon" />
    </label>
  </div>
</template>

<script>
import { v4 as uuid } from 'uuid'

export default {
  props: {
    name: {
      type: String,
      required: false,
      default: null
    },
    value: {
      type: [Boolean, Number, String],
      required: false,
      default: false
    },
    modelValue: {
      default: false
    },
    icon: {
      type: String,
      default: 'soho'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    color: {
      type: String,
      default: null
    }
  },

  computed: {
    backgroundColor () {
      return this.color && this.checked && this.disabled ?
        { background: this.color } : null
    },
    checked () {
      if (Array.isArray(this.modelValue)) {
        return this.modelValue.indexOf(this.value) > -1
      }
      return this.modelValue
    },
    statusIcon () {
      if (!this.checked && this.disabled) {
        return 'disabled'
      }
      return 'checkmark'
    }
  },

  data () {
    return {
      id: null
    }
  },

  methods: {
    onChange () {
      let value

      if (Array.isArray(this.modelValue)) {
        value = this.modelValue.slice(0)

        if (this.checked) {
          value.splice(value.indexOf(this.value), 1)
        } else {
          value.push(this.value)
        }
      } else {
        value = !this.checked
      }
      this.$emit('input', value)
    }
  },

  model: {
    prop: 'modelValue',
    event: 'input'
  },

  mounted () {
    this.id = `check-button-${uuid()}`
  }
}
</script>

<style lang="scss">

  div.check-button {
    width: 100%;
    padding-bottom: $space-030;

    input {
      @include visually-hidden();
    }

    .icon {
      @include transition-fast;
    }

    label {
      @include border-small();
      @include font('040');
      @include deprecated-f-color-dark;
      padding: $d-xx-small;
      display: flex;
      align-items: center;
      background: $color-white;
      cursor: pointer;
      @include transition-fast;
      position: relative;

      &:after {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border: 1px solid rgba($color-white, 0);
        @include transition-fast;
        opacity: 0;
        pointer-events: none;
        visibility: hidden;
      }

      .icon:first-child {
        margin-right: $d-xx-small;
        min-width: $d-med-small;
      }

      span {
        flex-grow: 1;
      }

      .icon.status {
        opacity: 0;
        pointer-events: none;
        visibility: hidden;
        transform: scale(0);
      }
    }

    input:checked + label {
      @include border-medium();

      .icon.status {
        opacity: 1;
        visibility: visible;
        transform: scale(.7);
      }
    }

    input[disabled]:not(:checked) + label {
      cursor: not-allowed;
      background-color: $deprecated-grey-x-light;

      .icon.status {
        display: block;
      }
    }

    input[disabled]:checked + label {
      cursor: not-allowed;
      box-shadow: none;
      @include f-color-white;

      .icon {
        filter: invert(1)
      }

      .icon.checkmark {
        opacity: 1;
        visibility: visible;
        transform: scale(.7);
      }
    }

    input:focus {
      & + label:after {
        opacity: 1;
        visibility: visible;
        pointer-events: none;
      }
    }
  }
</style>
